import { directoryClient } from 'shared/domain/directory/directoryClient';
import { documentationClient } from 'shared/domain/documentation/documentationClient';
import type { StateCreator } from 'zustand';
import { logChannelRequestFailure } from 'setup/logger/logChannelRequestFailure';
import { toJSDate } from 'shared/utils/date';
import { DataSlice, DataSliceState } from './types';
import type { FetchDirectoryContentOptions } from './types';
import type { DocumentationStoreSlices } from '../types';
import {
  buildViewRows,
  filterRenderableDocumentations,
  filterRenderableDirectories,
  isSearchActive,
} from '../filters/model';
import { buildFilteredDirectoryRequest } from './model';
import { areDirectoryIdsEqual } from 'shared/domain/directory/directoryIds';
import { createProjectChangeCheck } from '../moduleLifecycle/model';
import type { DocumentationValueGetters } from 'components/common/dataGrid/columnsFactory/valueGetters';
import { DocumentState } from 'components/common/dataGrid/columnsFactory/types';
import { DocumentationModel } from 'shared/domain/documentation/documentationModel';

export * from './types';

const INITIAL_DATA_STATE: DataSliceState = {
  allDirectories: [],
  currentDirectoryDirectories: [],
  currentDirectoryDocumentations: [],
  isLoadingAllDirectories: true,
  isLoadingCurrentDirDirectories: true,
  isLoadingCurrentDirDocumentations: true,
};

type DataSliceCreator = StateCreator<DocumentationStoreSlices, [], [], DataSlice>;

export const createDataSlice: DataSliceCreator = (set, get) => {
  /** The request in flight, for the project data generation it was made for. */
  let allDirectoriesRequest: { generation: number; promise: Promise<void> } | null = null;
  /** One read after the request in flight, shared by the callers that came during it. */
  let followUpAllDirectoriesRequest: Promise<void> | null = null;

  const requestAllDirectories = async (): Promise<void> => {
    set({ isLoadingAllDirectories: true });
    const hasProjectChanged = createProjectChangeCheck(get);

    try {
      const directories = await directoryClient.getAll();
      if (hasProjectChanged()) {
        return;
      }
      set({
        allDirectories: directories,
        isLoadingAllDirectories: false,
      });
    } catch (error) {
      logChannelRequestFailure('Error fetching all directories', error);
      if (!hasProjectChanged()) {
        set({ isLoadingAllDirectories: false });
      }
    }
  };

  return {
    ...INITIAL_DATA_STATE,

    /**
     * Reads every directory. A request in flight for the current project is not
     * joined: it may have read IndexedDB before the caller's change (a create,
     * a rename), so callers during it share one read after it. A request made
     * for another project (before a project change) is never joined.
     */
    fetchAllDirectories: (): Promise<void> => {
      const generation = get().projectDataGeneration;
      if (allDirectoriesRequest?.generation === generation) {
        followUpAllDirectoriesRequest ??= allDirectoriesRequest.promise.then(() => {
          followUpAllDirectoriesRequest = null;
          return get().fetchAllDirectories();
        });
        return followUpAllDirectoriesRequest;
      }
      const promise = requestAllDirectories().finally(() => {
        if (allDirectoriesRequest?.promise === promise) {
          allDirectoriesRequest = null;
        }
      });
      allDirectoriesRequest = { generation, promise };
      followUpAllDirectoriesRequest = null;
      return promise;
    },

    fetchDirectoryContent: async (
      directoryId: string,
      options?: FetchDirectoryContentOptions
    ): Promise<void> => {
      const suppressLoadingIndicators = options?.suppressLoadingIndicators === true;
      const updateVisibleRows = options?.updateVisibleRows !== false;
      const shouldUpdateVisibleRows = (): boolean =>
        updateVisibleRows && areDirectoryIdsEqual(get().currentDirectory, directoryId);

      // Search results own the visible rows - skip the directory request, but
      // clear the loading flags (they start as `true` and would block the grid).
      if (shouldUpdateVisibleRows() && isSearchActive(get())) {
        set({
          isLoadingCurrentDirDirectories: false,
          isLoadingCurrentDirDocumentations: false,
        });
        return;
      }

      if (shouldUpdateVisibleRows()) {
        if (!suppressLoadingIndicators) {
          set({
            isLoadingCurrentDirDirectories: true,
            isLoadingCurrentDirDocumentations: true,
          });
        }
      }

      const { filterModel, documentState } = get();
      const hasProjectChanged = createProjectChangeCheck(get);

      try {
        // Fetch filtered data via SW - handles all filtering including state filter
        const { directories, documentations } = await documentationClient.getFilteredDirectory(
          buildFilteredDirectoryRequest({ parentId: directoryId, filterModel, documentState })
        );

        const validDirectories = filterRenderableDirectories(directories);
        const validDocumentations = filterRenderableDocumentations(documentations);

        const viewItems = buildViewRows({
          directories: validDirectories,
          documentations: validDocumentations,
          documentState,
        });
        if (hasProjectChanged()) {
          return;
        }

        if (shouldUpdateVisibleRows()) {
          set({
            currentDirectoryDirectories: validDirectories,
            currentDirectoryDocumentations: validDocumentations,
            isLoadingCurrentDirDirectories: false,
            isLoadingCurrentDirDocumentations: false,
          });
          // Checked when the fetch resolves: a search may have started meanwhile
          // (or a background refresh ran during search) - keep its results.
          if (!isSearchActive(get())) {
            get().commitVisibleRows(viewItems);
            set({ isFiltering: false });
          }
        }
      } catch (error) {
        logChannelRequestFailure('Error fetching directory content', error);
        if (shouldUpdateVisibleRows() && !suppressLoadingIndicators) {
          set({
            isLoadingCurrentDirDirectories: false,
            isLoadingCurrentDirDocumentations: false,
            isFiltering: false,
          });
        }
      }
    },

    resetDataState: (): void => {
      set(INITIAL_DATA_STATE);
    },

    clearCurrentDirectoryContent: (): void => {
      set({
        currentDirectoryDirectories: [],
        currentDirectoryDocumentations: [],
        filteredRows: [],
        baseRows: [],
        isLoadingCurrentDirDirectories: true,
        isLoadingCurrentDirDocumentations: true,
      });
    },

    resyncAllDirectories: async (): Promise<void> => {
      await get().fetchAllDirectories();
    },
  };
};
